import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Star, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFeedback() {
    const { user, token, loading } = useAuth();
    const [busy, setBusy] = useState(true);
    const [data, setData] = useState({
        items: [],
        stats: { total: 0, rated: 0, avg_rating: null },
    });

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        setBusy(true);
        axios
            .get(`${API}/admin/feedback`, { headers })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load feedback"))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!user) return <Navigate to="/admin/login" replace />;

    const { items, stats } = data;

    return (
        <AdminShell>
            <div className="max-w-5xl">
                <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-ember" />
                    <p className="text-xs uppercase tracking-[0.25em] text-ember">
                        Briefcase · Client feedback
                    </p>
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[0.95] mb-10">
                    What clients
                    <br />
                    <span className="font-serif-i italic text-ember">
                        are saying.
                    </span>
                </h1>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-10">
                    <Stat
                        label="Total responses"
                        value={busy ? "—" : stats.total}
                    />
                    <Stat
                        label="Rated"
                        value={busy ? "—" : stats.rated}
                    />
                    <Stat
                        label="Avg rating"
                        value={
                            busy
                                ? "—"
                                : stats.avg_rating !== null
                                  ? `${stats.avg_rating} / 5`
                                  : "—"
                        }
                        accent
                    />
                </div>

                {busy ? (
                    <div className="text-bone-500 text-sm flex items-center gap-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-bone-500 text-sm border border-dashed border-white/10 p-12 text-center">
                        No feedback submissions yet.
                    </p>
                ) : (
                    <ul
                        data-testid="admin-feedback-list"
                        className="space-y-5"
                    >
                        {items.map((f) => (
                            <li
                                key={f.id}
                                data-testid={`admin-feedback-${f.id}`}
                                className="border border-white/10 bg-ink-800/40 p-6"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                                    <div>
                                        <p className="font-display text-bone text-lg tracking-tight">
                                            {f.client_name}
                                            {f.client_company && (
                                                <span className="text-bone-500 font-sans text-sm font-normal ml-2">
                                                    · {f.client_company}
                                                </span>
                                            )}
                                        </p>
                                        <a
                                            href={`mailto:${f.client_email}`}
                                            className="text-xs text-bone-500 hover:text-ember transition-colors inline-flex items-center gap-1"
                                        >
                                            <Mail className="w-3 h-3" />
                                            {f.client_email}
                                        </a>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500 px-2 py-1 border border-white/15">
                                        {f.project_title
                                            ? `Project: ${f.project_title}`
                                            : "Studio overall"}
                                    </span>
                                </div>

                                {f.rating && (
                                    <div className="flex items-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                className={`w-4 h-4 ${
                                                    n <= f.rating
                                                        ? "fill-ember text-ember"
                                                        : "text-bone-500"
                                                }`}
                                            />
                                        ))}
                                        <span className="text-xs text-bone-500 ml-2">
                                            {f.rating} / 5
                                        </span>
                                    </div>
                                )}

                                {f.body && (
                                    <p className="text-bone text-sm leading-relaxed whitespace-pre-wrap">
                                        {f.body}
                                    </p>
                                )}

                                <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-3">
                                    {new Date(f.created_at).toLocaleString()}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </AdminShell>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div
            className={`p-5 border ${accent ? "border-ember/50 bg-ember/5" : "border-white/10 bg-ink-800/40"}`}
        >
            <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </p>
            <p
                className={`font-display tracking-tight text-2xl ${accent ? "text-ember" : "text-bone"}`}
            >
                {value}
            </p>
        </div>
    );
}
